import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useGetOneWorker } from "#src/components/hooks/adapters/useWorkers";
import { WorkerDetailRoutePath } from "#src/routes/workers/definitions/[workerId]/detail";
import {
  Accordion,
  EmptyState,
  KeyValueList,
  type KeyValuePairList,
  Skeleton,
} from "@validereinc/common-components";
import type { WorkerRunInputValues } from "@validereinc/domain";
import React, { useMemo } from "react";
import { recordToKeyValuePairs } from "../Utils/WorkflowConfigurationRunWorker.helpers";

const { AccordionPanel } = Accordion;

type WorkflowConfigurationRunWorkerPreviewProps = {
  workerId?: string;
  workerVersionId?: number;
  workerRunInput?: Record<string, WorkerRunInputValues>;
  output?: Record<string, string>;
};

/**
 * Converts a worker_run_input record (which may contain non-string values)
 * into the format expected by KeyValueList.
 */
const workerRunInputToKeyValuePairs = (
  record?: Record<string, WorkerRunInputValues> | null
): KeyValuePairList =>
  record
    ? Object.entries(record).map(([key, value]) => ({
        title: key,
        value:
          value == null
            ? "-"
            : Array.isArray(value)
              ? value.join(", ") || "-"
              : String(value) || "-",
      }))
    : [];

/**
 * Read-only preview of a run_worker system action configuration
 * in the workflow builder's preview mode. Shows worker name, version,
 * and configured worker_run_input/output key-value pairs.
 */
export const WorkflowConfigurationRunWorkerPreview = ({
  workerId,
  workerVersionId,
  workerRunInput,
  output,
}: WorkflowConfigurationRunWorkerPreviewProps) => {
  const workerQuery = useGetOneWorker(
    { id: workerId ?? "" },
    { enabled: Boolean(workerId) }
  );
  const workerName = workerQuery.data?.data?.name;

  const isLoading = Boolean(workerId) && workerQuery.isLoading;

  const overviewData: KeyValuePairList = useMemo(() => {
    const workerDisplayLabel = workerName ?? workerId ?? "-";
    const versionDisplayLabel =
      workerVersionId != null ? `v${workerVersionId}` : "-";

    return [
      {
        title: "Worker",
        value: workerId ? (
          <RoutingLink
            to={WorkerDetailRoutePath.toLinkParts({
              pathParams: { workerId },
            })}
          >
            {workerDisplayLabel}
          </RoutingLink>
        ) : (
          workerDisplayLabel
        ),
      },
      {
        title: "Version",
        value:
          workerId && workerVersionId != null ? (
            <RoutingLink
              to={WorkerDetailRoutePath.toLinkParts({
                pathParams: {
                  workerId,
                },
                queryParams: { version: String(workerVersionId) },
              })}
            >
              {versionDisplayLabel}
            </RoutingLink>
          ) : (
            versionDisplayLabel
          ),
      },
    ];
  }, [workerId, workerName, workerVersionId]);

  const inputData = useMemo(
    () => workerRunInputToKeyValuePairs(workerRunInput),
    [workerRunInput]
  );
  const outputData = useMemo(() => recordToKeyValuePairs(output), [output]);

  return (
    <Skeleton isLoading={isLoading}>
      <Accordion
        defaultActiveKeys={["worker-overview", "worker-input", "worker-output"]}
      >
        <AccordionPanel
          dataKey="worker-overview"
          title="Worker Overview"
        >
          <KeyValueList
            data={overviewData}
            variant="shaded"
          />
        </AccordionPanel>

        <AccordionPanel
          dataKey="worker-input"
          title="Input"
        >
          {inputData.length > 0 ? (
            <KeyValueList
              data={inputData}
              variant="shaded"
            />
          ) : (
            <EmptyState title="No input parameters configured." />
          )}
        </AccordionPanel>

        <AccordionPanel
          dataKey="worker-output"
          title="Output"
        >
          {outputData.length > 0 ? (
            <KeyValueList
              data={outputData}
              variant="shaded"
            />
          ) : (
            <EmptyState title="No output parameters configured." />
          )}
        </AccordionPanel>
      </Accordion>
    </Skeleton>
  );
};
